import { closeDialog } from '@/lib/dialogMotion'
import { useSearchParams } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import Layout from '@/components/Layout'
import { FilterSelect, DateTimeFilter } from '@/components/FilterDropdown'
import { usersApi, type Audit } from '@/api/users'
import { errorMessage } from '@/api/client'

const names: Record<string, string> = {
  'billing.package.save': '套餐配置',
  'billing.order.reconcile.request': '发起查单补单',
  'billing.order.reconcile': '查单补单结果',
  'billing.order.credit_repair.request': '发起已支付订单补记',
  'billing.refund.review.request': '发起退款审核',
  'billing.refund.review': '退款审核结果',
  login: '登录',
  logout: '退出',
  'user.delete': '永久删除用户',
  'user.unbind_wechat': '微信解绑',
  'user.status': '账号启停',
  'user.recharge': '手动充值',
  'user.reset_password': '重置密码',
}
function actionName(a: Audit) {
  if (a.action === 'user.recharge' && a.after && typeof a.after === 'object') {
    const after = a.after as { credit_kind?: string; request?: { credit_kind?: string } }
    if (after.credit_kind === 'team_grant' || after.request?.credit_kind === 'team_grant') return '团队人工赠点'
  }
  return names[a.action] || a.action
}
export default function AuditLogs({
  recharges = false,
}: {
  recharges?: boolean
}) {
  const [params] = useSearchParams()
  const keyword = params.get('keyword') || ''
  const [copyStatus, setCopyStatus] = useState('')
  const dialog = useRef<HTMLDialogElement>(null)
  const [selected, setSelected] = useState<Audit | null>(null)
  const cache = useQueryClient()
  const [page, setPage] = useState(1)
  const empty = {
    target_id: '',
    admin: '',
    action: '',
    keyword,
    outcome: '',
    from: '',
    to: '',
  }
  const [input, setInput] = useState<Record<string, string>>(empty)
  const [filters, setFilters] = useState<Record<string, string>>(empty)
  useEffect(() => { setInput(prev => ({ ...prev, keyword })); setFilters(prev => ({ ...prev, keyword })); setPage(1) }, [keyword])
  function change(key: string, value: string) {
    setInput((prev) => ({ ...prev, [key]: value }))
    if (!value.trim()) {
      setFilters((prev) => ({ ...prev, [key]: '' }))
      setPage(1)
      void cache.invalidateQueries({ queryKey: ['audit'] })
    }
  }
  const query = useQuery({
    queryKey: ['audit', recharges, page, filters],
    queryFn: () => usersApi.audit(page, filters, recharges),
  })
  return (
    <Layout>
      <h1 className="mb-4 text-2xl font-semibold">
        {recharges ? '充值记录' : '操作记录'}
      </h1>
      <form
        className="mb-4 flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault()
          setFilters(
            Object.fromEntries(
              Object.entries(input).map(([k, v]) => [
                k,
                (k === 'from' || k === 'to') && v
                  ? new Date(v).toISOString()
                  : v.trim(),
              ]),
            ),
          )
          setPage(1)
        }}
      >
        {[
          ['target_id', '目标用户 ID'],
          ['admin', '操作者'],
          ['keyword', '关键词'],
        ].map(([key, label]) => (
          <label
            className="min-w-0 flex-1 basis-40 text-xs text-slate-600"
            key={key}
          >
            {label}
            <input
              aria-label={label}
              placeholder={key === 'target_id' ? '输入用户 ID / PPID' : key === 'keyword' ? '昵称、用户名、操作原因' : '输入操作者账号'}
              title={key === 'target_id' ? '用户 ID / PPID，支持部分匹配' : key === 'keyword' ? '可搜索昵称、用户名、PPID、操作原因、操作者和操作类型' : '操作者账号'}
              className="mt-1 h-10 w-full min-w-0 rounded-lg border px-3 text-sm"
              maxLength={key === 'admin' ? 64 : key === 'target_id' ? 128 : 200}
              value={input[key]}
              onChange={(e) => change(key, e.target.value)}
            />
          </label>
        ))}
        {!recharges && <FilterSelect label="操作类型" value={input.action} options={[["", "全部类型"], ...Object.entries(names)]} onChange={(value) => change('action', value)} />}
        <FilterSelect label="操作结果" value={input.outcome} options={[["", "全部结果"], ["success", "成功"], ["failure", "失败"]]} onChange={(value) => change('outcome', value)} />
        <DateTimeFilter label="开始时间" value={input.from} onChange={(value) => change('from', value)} />
        <DateTimeFilter label="结束时间（不含）" value={input.to} onChange={(value) => change('to', value)} />
        <button className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white">
          查询
        </button>
        <button
          type="button"
          className="rounded-lg border px-4 py-2 text-sm"
          onClick={() => {
            setInput(empty)
            setFilters(empty)
            setPage(1)
            void cache.invalidateQueries({ queryKey: ['audit'] })
          }}
        >
          重置
        </button>
      </form>
      {query.isError && (
        <p role="alert" className="text-red-700">
          {errorMessage(query.error)}{' '}
          <button onClick={() => query.refetch()}>重试</button>
        </p>
      )}
      {query.isPending ? (
        <p>加载中…</p>
      ) : (
        query.data && (
          <>
            <div className="min-h-64 flex-1 overflow-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
              <table className="w-full min-w-[880px] text-left text-sm">
                <thead className="sticky top-0 bg-slate-100 text-slate-600">
                  <tr>
                    {[
                      '时间',
                      '操作人',
                      '操作',
                      '目标用户',
                      '结果 / 原因 / 详情',
                    ].map((s) => (
                      <th key={s} className="p-3">
                        {s}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {query.data.items.map((a) => (
                    <tr key={a.id} className="border-t align-top">
                      <td className="whitespace-nowrap p-3">
                        {new Date(a.created_at).toLocaleString()}
                      </td>
                      <td className="p-3">{a.admin}</td>
                      <td className="p-3">{actionName(a)}</td>
                      <td className="max-w-52 break-all p-3"><p className="font-medium text-slate-900">{a.target_name || (a.target_id ? '—' : '不适用')}</p><p className="mt-1 text-xs text-slate-500">{a.target_id || '—'}</p></td>
                      <td className="p-3">
                        <p
                          className={
                            a.outcome === 'failure'
                              ? 'text-red-700'
                              : 'text-green-700'
                          }
                        >
                          {a.outcome === 'failure' ? '失败' : '成功'}
                        </p>
                        <p>{a.reason || '—'}</p>
                        {a.error_message && (
                          <p className="text-red-700">{a.error_message}</p>
                        )}
                        <button
                          type="button"
                          className="mt-1 text-brand-600 hover:underline"
                          onClick={(event) => {
                            const rect = event.currentTarget.closest('tr')!.getBoundingClientRect()
                            document.documentElement.style.setProperty('--detail-origin-x', (rect.left + rect.width / 2 - window.innerWidth / 2) + 'px')
                            document.documentElement.style.setProperty('--detail-origin-y', (rect.top + rect.height / 2 - window.innerHeight / 2) + 'px')
                            setCopyStatus('')
                            setSelected(a)
                            dialog.current?.showModal()
                          }}
                        >
                          变更详情
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!query.data.items.length && (
                <p className="p-8 text-center text-slate-500">暂无操作记录</p>
              )}
            </div>
            <div className="mt-4 flex shrink-0 items-center justify-between text-sm">
              <span>
                共 {query.data.total} 条 · 第 {page} 页
              </span>
              <div className="flex gap-4">
                <button
                  disabled={page === 1}
                  className="disabled:opacity-30"
                  onClick={() => setPage(page - 1)}
                >
                  上一页
                </button>
                <button
                  disabled={page * 20 >= query.data.total}
                  className="disabled:opacity-30"
                  onClick={() => setPage(page + 1)}
                >
                  下一页
                </button>
              </div>
            </div>
          </>
        )
      )}
      <dialog
        ref={dialog}
        aria-labelledby="audit-detail-title"
        onCancel={(event) => { event.preventDefault(); void closeDialog(dialog.current) }}
        className="row-detail-dialog fixed m-auto max-h-[85dvh] w-[calc(100%-2rem)] max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white p-0 shadow-xl backdrop:bg-slate-900/40"
        onClick={(event) => {
          if (event.target === event.currentTarget) void closeDialog(dialog.current)
        }}
      >
        <div className="flex max-h-[85dvh] flex-col" onClick={(event) => event.stopPropagation()}>
          <div className="flex shrink-0 items-center justify-between border-b px-6 py-4">
            <h2 id="audit-detail-title" className="text-lg font-semibold">变更详情</h2>
            <button type="button" autoFocus className="rounded-lg border px-3 py-1 text-sm" onClick={() => void closeDialog(dialog.current)}>关闭</button>
          </div>
          {selected && (
            <div className="min-h-0 overflow-auto overscroll-contain p-6 text-sm">
              <p className="font-medium">{names[selected.action] || selected.action} · {selected.target_name || selected.target_id || '不适用'}</p>
              {selected.target_name && <p className="mt-1 break-all text-xs text-slate-500">{selected.target_id}</p>}
              <p className="mt-2 text-slate-600">{selected.admin} · {new Date(selected.created_at).toLocaleString()}</p>
              <p className="mt-2 break-words">操作原因：{selected.reason || '—'}</p>
              {selected.error_message && <p className="mt-2 text-red-700">{selected.error_message}</p>}
              <pre className="mt-4 overflow-auto rounded-lg bg-slate-50 p-4 text-xs">{JSON.stringify({ before: selected.before, after: selected.after, ip: selected.ip }, null, 2)}</pre>
            </div>
          )}
          <div className="flex shrink-0 items-center justify-end gap-3 border-t px-6 py-4">
            <span role="status" className="text-sm text-slate-600">{copyStatus}</span>
            <button
              type="button"
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white"
              onClick={async () => {
                if (!selected) return
                try {
                  await navigator.clipboard.writeText(JSON.stringify({ before: selected.before, after: selected.after, ip: selected.ip }, null, 2))
                  setCopyStatus('已复制')
                } catch {
                  setCopyStatus('复制失败，请手动选择内容复制')
                }
              }}
            >复制详情</button>
          </div>
        </div>
      </dialog>
    </Layout>
  )
}




